import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Megaphone, Plus, Loader2, X, Eye, Users, CheckCheck, Archive, RotateCcw,
} from 'lucide-react'
import { apiGet, cachedGet, postJson, patchJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { usePersistedState } from '../../lib/persist.js'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import AudiencePicker from '../../components/broadcast/AudiencePicker.jsx'
import ReachPanel from '../../components/broadcast/ReachPanel.jsx'
import { audienceSummary, hasAudience, statusMeta, toneClass } from '../../lib/broadcast.js'
import { doctorCode, doctorFullName } from '../../lib/dentist.js'

/**
 * ประกาศ (/manager/broadcast) — text announcements to doctors.
 *
 * Its own section rather than a tab on ตารางเวร: an announcement is
 * communication, not roster work, and the only thing it shares with ขอเวร is the
 * audience picker.
 *
 * ⚠️ There is no edit. An announcement people have already read is a record of
 * what was said, not a document — the two things that still make sense
 * afterwards are closing it and withdrawing it, and both are here.
 */

const CARD = 'rounded-2xl border border-slate-200 bg-white'
const INPUT = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

const EMPTY_TARGET = { target_all: false, target_spec: [], target_doctor: [] }

export default function Broadcast() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [statusFilter, setStatusFilter] = usePersistedState('broadcast:status', '')
  const [composing, setComposing] = useState(false)
  const [detailId, setDetailId] = useState(null)
  const [doctors, setDoctors] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const r = await apiGet('/api/hr/broadcast', statusFilter ? { status: statusFilter } : undefined)
      setRows(Array.isArray(r?.items) ? r.items : [])
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดรายการประกาศไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => { load() }, [load])

  // The slim doctor list is shared with /manager/dentist through the session
  // cache, so opening the composer costs nothing extra.
  useEffect(() => {
    let alive = true
    cachedGet('/api/doctors', { slim: 1 })
      .then((d) => { if (alive) setDoctors(Array.isArray(d) ? d : (d?.items || [])) })
      .catch(() => { /* the picker degrades to empty option lists */ })
    return () => { alive = false }
  }, [])

  return (
    <div className="px-10 py-8">
      <div className="flex items-start gap-3 mb-1">
        <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight">ประกาศ</h1>
        <button
          onClick={() => setComposing(true)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-3 py-2 text-[13.5px] font-medium text-white hover:bg-brand-700 transition-colors"
        >
          <Plus size={15} strokeWidth={2} />
          เขียนประกาศ
        </button>
      </div>
      <p className="text-[13.5px] text-slate-500 mb-5">
        ส่งข้อความถึงทันตแพทย์ — ทุกท่าน เลือกตามความเชี่ยวชาญ หรือระบุรายบุคคล
      </p>

      <div className="flex items-center gap-2 mb-4">
        {[['', 'ทั้งหมด'], ['open', 'กำลังแสดง'], ['closed', 'ปิดแล้ว']].map(([v, label]) => (
          <button
            key={v || 'all'}
            onClick={() => setStatusFilter(v)}
            className={`rounded-lg px-2.5 py-1.5 text-[13px] transition-colors ${
              statusFilter === v ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100' : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {err && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{err}</div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-[13.5px] text-slate-500 py-10 justify-center">
          <Loader2 size={16} className="animate-spin" /> กำลังโหลด…
        </div>
      ) : rows.length === 0 ? (
        <div className={`${CARD} px-6 py-12 text-center`}>
          <Megaphone size={28} strokeWidth={1.5} className="mx-auto text-slate-300 mb-2" />
          <p className="text-[14px] text-slate-500">ยังไม่มีประกาศ</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <AnnouncementRow key={r.id} row={r} onOpen={() => setDetailId(r.id)} />
          ))}
        </div>
      )}

      {composing && (
        <ComposerDrawer
          doctors={doctors}
          onClose={() => setComposing(false)}
          onSent={() => { setComposing(false); load() }}
        />
      )}

      {detailId != null && (
        <DetailDrawer
          id={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => { load() }}
        />
      )}
    </div>
  )
}

function AnnouncementRow({ row, onOpen }) {
  const meta = statusMeta('announcement', row.status)
  const total = Number(row.reach_total || 0)
  const login = Number(row.reach_login || 0)
  const read = Number(row.read_count || 0)
  return (
    <button
      onClick={onOpen}
      className={`${CARD} w-full text-left px-4 py-3 hover:border-slate-300 transition-colors`}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[14.5px] font-semibold text-slate-900 truncate">{row.title}</span>
            <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11.5px] ring-1 ${toneClass(meta.tone)}`}>
              {meta.label}
            </span>
          </div>
          <p className="text-[13px] text-slate-500 mt-0.5 line-clamp-1">{row.body}</p>
          <p className="text-[12px] text-slate-400 mt-1">
            {audienceSummary(row)}
            {row.created_by_name ? ` · โดย ${row.created_by_name}` : ''}
          </p>
        </div>
        <div className="shrink-0 text-right">
          {/* Read out of REACHABLE, not out of total — 12/42 อ่านแล้ว is a real
              number; 12/137 quietly blames the doctors for an account problem. */}
          <div className="flex items-center gap-1 justify-end text-[13px] text-slate-700">
            <CheckCheck size={13} strokeWidth={1.75} className="text-emerald-600" />
            {read}/{login} อ่านแล้ว
          </div>
          {total > login && (
            <div className="text-[11.5px] text-amber-600 mt-0.5">ไม่ถึง {total - login} ท่าน</div>
          )}
        </div>
      </div>
    </button>
  )
}

/* ── composer ────────────────────────────────────────────────────────────── */

function ComposerDrawer({ doctors, onClose, onSent }) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [target, setTarget] = useState(EMPTY_TARGET)
  const [preview, setPreview] = useState(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewErr, setPreviewErr] = useState('')
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState('')
  const [confirm, setConfirm] = useState(false)
  const seq = useRef(0)

  const ready = hasAudience(target)

  // Reach is recomputed server-side on every targeting change, debounced. It is
  // never derived in the browser: a second implementation of the spec-matching
  // rule would drift from the one that actually decides who gets the message.
  useEffect(() => {
    if (!ready) { setPreview(null); setPreviewErr(''); return }
    const mine = ++seq.current
    setPreviewing(true)
    const t = setTimeout(() => {
      postJson('/api/hr/broadcast/preview', target)
        .then((r) => { if (seq.current === mine) { setPreview(r); setPreviewErr('') } })
        .catch((e) => { if (seq.current === mine) { setPreview(null); setPreviewErr(apiErrorText(e, 'คำนวณผู้รับไม่สำเร็จ')) } })
        .finally(() => { if (seq.current === mine) setPreviewing(false) })
    }, 250)
    return () => clearTimeout(t)
  }, [target, ready])

  const canSend = ready && title.trim() && body.trim() && !sending

  async function send() {
    setConfirm(false)
    setSending(true)
    setErr('')
    try {
      await postJson('/api/hr/broadcast', { title: title.trim(), body: body.trim(), ...target })
      onSent()
    } catch (e) {
      setErr(apiErrorText(e, 'ส่งประกาศไม่สำเร็จ'))
    } finally {
      setSending(false)
    }
  }

  const reachable = Number(preview?.reachable || 0)

  return (
    <Drawer title="เขียนประกาศ" onClose={onClose} wide>
      <div className="space-y-4">
        <div>
          <label className="block text-[13px] font-medium text-slate-600 mb-1.5">หัวข้อ</label>
          <input className={INPUT} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="เช่น ปิดทำการช่วงสงกรานต์" />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-slate-600 mb-1.5">เนื้อหา</label>
          <textarea
            className={`${INPUT} min-h-[140px] resize-y leading-relaxed`}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="พิมพ์ข้อความที่ต้องการแจ้งคุณหมอ…"
          />
        </div>

        <AudiencePicker value={target} onChange={setTarget} doctors={doctors} disabled={sending} />

        <ReachPanel preview={preview} loading={previewing} error={previewErr} />

        {err && <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{err}</div>}

        <div className="flex items-center gap-2 pt-1">
          <button
            disabled={!canSend}
            onClick={() => setConfirm(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-4 py-2 text-[13.5px] font-medium text-white hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Megaphone size={15} strokeWidth={2} />}
            ส่งประกาศ
          </button>
          <button onClick={onClose} className="rounded-xl px-3 py-2 text-[13.5px] text-slate-500 hover:bg-slate-50 transition-colors">
            ยกเลิก
          </button>
        </div>
      </div>

      {/* The confirm restates the reach rather than asking "are you sure" — the
          number is the decision, and it is the last chance to notice that a
          send aimed at 137 doctors will actually land on 42. */}
      {confirm && (
        <ConfirmDialog
          title="ส่งประกาศนี้?"
          message={
            reachable > 0
              ? `“${title.trim()}” จะแสดงกับคุณหมอ ${reachable} ท่านที่เข้าระบบได้ — ส่งแล้วแก้ข้อความไม่ได้`
              : 'ยังไม่มีคุณหมอที่เข้าระบบได้ในกลุ่มนี้ ประกาศจะถูกบันทึกไว้แต่จะไม่มีใครเห็น'
          }
          confirmLabel="ส่งประกาศ"
          tone={reachable > 0 ? 'default' : 'danger'}
          onConfirm={send}
          onCancel={() => setConfirm(false)}
        />
      )}
    </Drawer>
  )
}

/* ── detail ──────────────────────────────────────────────────────────────── */

function DetailDrawer({ id, onClose, onChanged }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmWithdraw, setConfirmWithdraw] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await apiGet(`/api/hr/broadcast/${id}`))
      setErr('')
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดประกาศไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  async function patch(payload) {
    setBusy(true)
    try {
      await patchJson(`/api/hr/broadcast/${id}`, payload)
      await load()
      onChanged?.()
    } catch (e) {
      setErr(apiErrorText(e, 'บันทึกไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  const a = data?.announcement
  const people = data?.people || []
  const readPeople = people.filter((p) => p.read_at)
  const unread = people.filter((p) => !p.read_at && p.can_login)
  const noAccount = people.filter((p) => !p.can_login)

  return (
    <Drawer title={a?.title || 'ประกาศ'} onClose={onClose} wide>
      {loading ? (
        <div className="flex items-center gap-2 text-[13.5px] text-slate-500 py-8 justify-center">
          <Loader2 size={16} className="animate-spin" /> กำลังโหลด…
        </div>
      ) : err ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{err}</div>
      ) : (
        <div className="space-y-4">
          <div className={`${CARD} px-4 py-3`}>
            <p className="text-[13.5px] text-slate-800 whitespace-pre-wrap leading-relaxed">{a?.body}</p>
            <p className="text-[12px] text-slate-400 mt-3">
              {audienceSummary(a)}{a?.created_by_name ? ` · โดย ${a.created_by_name}` : ''}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Stat label="อ่านแล้ว" value={readPeople.length} tone="emerald" />
            <Stat label="ยังไม่อ่าน" value={unread.length} tone="amber" />
            <Stat label="ไม่มีบัญชี" value={noAccount.length} tone="slate" />
          </div>

          <PeopleList title="อ่านแล้ว" icon={Eye} people={readPeople} showTime />
          <PeopleList title="ยังไม่อ่าน" icon={Users} people={unread} />
          {/* Kept as its own group, never merged into "ยังไม่อ่าน": these
              doctors did not ignore the announcement, they cannot open it. */}
          <PeopleList
            title="เข้าระบบไม่ได้ — ไม่เห็นประกาศนี้"
            icon={Users}
            people={noAccount}
            tone="amber"
          />

          <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
            {a?.status === 'open' ? (
              <button disabled={busy} onClick={() => patch({ status: 'closed' })}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-[13.5px] text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                <Archive size={14} strokeWidth={1.75} /> ปิดประกาศ
              </button>
            ) : (
              <button disabled={busy} onClick={() => patch({ status: 'open' })}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-[13.5px] text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                <RotateCcw size={14} strokeWidth={1.75} /> เปิดแสดงอีกครั้ง
              </button>
            )}
            <button disabled={busy} onClick={() => setConfirmWithdraw(true)}
              className="ml-auto rounded-xl px-3 py-2 text-[13.5px] text-rose-600 hover:bg-rose-50 disabled:opacity-50 transition-colors">
              ถอนประกาศ
            </button>
          </div>
        </div>
      )}

      {confirmWithdraw && (
        <ConfirmDialog
          title="ถอนประกาศนี้?"
          message={`“${a?.title || ''}” จะหายจากระบบของคุณหมอทุกท่าน รวมถึงคนที่อ่านไปแล้ว ${readPeople.length} ท่าน`}
          confirmLabel="ถอนประกาศ"
          tone="danger"
          onConfirm={() => { setConfirmWithdraw(false); patch({ active: false }).then(onClose) }}
          onCancel={() => setConfirmWithdraw(false)}
        />
      )}
    </Drawer>
  )
}

function Stat({ label, value, tone }) {
  return (
    <div className={`rounded-xl px-3 py-2 ring-1 ${toneClass(tone)}`}>
      <div className="text-[19px] font-semibold leading-tight">{value}</div>
      <div className="text-[12px] opacity-80">{label}</div>
    </div>
  )
}

function PeopleList({ title, icon: Icon, people, showTime = false, tone = 'slate' }) {
  if (!people.length) return null
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5 text-[12.5px] text-slate-500">
        <Icon size={13} strokeWidth={1.75} />
        <span>{title}</span>
        <span className="text-slate-400">({people.length})</span>
      </div>
      <ul className={`rounded-xl ring-1 divide-y ${tone === 'amber' ? 'ring-amber-100 divide-amber-100 bg-amber-50/50' : 'ring-slate-200 divide-slate-100 bg-white'}`}>
        {people.map((p) => (
          <li key={p.doctor_id} className="px-3 py-1.5 flex items-baseline gap-2 text-[13px] text-slate-700">
            <span className="truncate">{doctorCode(p) || doctorFullName(p) || `หมอ #${p.doctor_id}`}</span>
            {showTime && p.read_at && (
              <span className="ml-auto shrink-0 text-[11.5px] text-slate-400">
                {new Date(Number(p.read_at)).toLocaleString('th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {!p.can_login && (
              <span className="ml-auto shrink-0 text-[11.5px] text-amber-700">
                {p.user_id ? 'บัญชีปิดอยู่' : 'ยังไม่มีบัญชี'}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ── shell ───────────────────────────────────────────────────────────────── */

function Drawer({ title, children, onClose, wide = false }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/25" onClick={onClose} />
      <aside className={`absolute right-0 top-0 bottom-0 w-full bg-white shadow-xl flex flex-col ${wide ? 'max-w-[620px]' : 'max-w-[460px]'}`}>
        <header className="flex items-center gap-3 px-5 h-14 border-b border-slate-200 shrink-0">
          <h2 className="text-[15px] font-semibold text-slate-900 truncate">{title}</h2>
          <button onClick={onClose} className="ml-auto p-1.5 -mr-1.5 text-slate-400 hover:text-slate-700 transition-colors">
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
      </aside>
    </div>
  )
}
